import { defineStore } from 'pinia';
import { ref } from 'vue';
import {
  getStorage,
  readStorageJSON,
  writeStorageJSON
} from '@/utils/secure-storage';

export const THEME_STORAGE_KEY = 'h5:theme-colors';
export const DEFAULT_BRAND_NAME = 'ZorPay';

const LEGACY_BRAND_NAME = 'winpay';
const activeBrandName = ref(DEFAULT_BRAND_NAME);
let brandNameChangeListener = null;

// Keep this object limited to the Figma color repository. Runtime aliases such
// as theme and primaryColor resolve to these palette entries below.
export const DEFAULT_THEME_COLORS = Object.freeze({
  brand1: '#7442E8',
  brand2: '#8B5CF6',
  brand3: '#AA7AFF',
  brand4: '#C6A8FF',
  brand5: '#ECEFF4',
  brand6: '#EEE5FF',
  activityBackground: '#472A8A',
  button1: 'var(--wp-color-brand-1)',
  button2: '#E7E5FF',
  button3: '#ECEDFF',
  button4: '#F0F0F0',
  card1: '#F1EAFF',
  card2: '#F7F8FF',
  card3: '#F7F3FF',
  danger: '#C63333',
  mask: 'rgba(0, 0, 0, 0.6)',
  shadow1: '#DDCCFF',
  virtualCardStart: '#AA7AFF',
  virtualCardEnd: 'var(--wp-color-brand-1)',
  physicalCardStart: '#09BB7D',
  physicalCardEnd: '#09818A',
  physicalCard: 'var(--wp-color-physical-card-start)',
  physicalCardDark: '#086D4A'
});

const COLOR_VARIABLES = Object.freeze(Object.fromEntries(
  Object.keys(DEFAULT_THEME_COLORS).map((key) => [key, `--wp-color-${toKebabCase(key)}`])
));

const COLOR_ALIASES = Object.freeze({
  theme: 'brand1',
  themeNew: 'brand1',
  primary: 'brand1',
  primaryColor: 'brand1',
  main: 'brand1',
  mainColor: 'brand1',
  color: 'brand1',
  themeColor: 'brand1',
  button1: 'brand1',
  virtualCardEnd: 'brand1',
  themeNewColor: 'brand1',
  virtualCard: 'brand1',
  physicalCard: 'physicalCardStart',
  physicalCardMain: 'physicalCardStart'
});

const LEGACY_COLOR_REPLACEMENTS = Object.freeze({
  '#2667FF': '#7442E8'
});

const FLAT_CARD_TYPE_COLOR_KEYS = Object.freeze({
  VirtualCard: ['virtualCardStart', 'virtualCardEnd'],
  PhysicalCard: ['physicalCardStart', 'physicalCardEnd'],
  'PhysicalCard-2': ['physicalCardStart'],
  'PhysicalCard-3': ['physicalCardDark']
});

function toKebabCase(value) {
  return String(value || '')
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/([a-zA-Z])(\d+)/g, '$1-$2')
    .replace(/[_\s]+/g, '-')
    .toLowerCase();
}

function normalizeHex(value) {
  let normalized = String(value ?? '').trim().toUpperCase();
  if (/^[0-9A-F]{3,8}$/.test(normalized)) {
    normalized = `#${normalized}`;
  }
  if (/^#[0-9A-F]{3,4}$/.test(normalized)) {
    return `#${normalized.slice(1).split('').map((item) => `${item}${item}`).join('')}`;
  }
  if (/^#[0-9A-F]{6}(?:[0-9A-F]{2})?$/.test(normalized)) {
    return normalized;
  }
  return '';
}

function normalizeCssColor(value) {
  const normalized = normalizeHex(value);
  if (normalized) {
    return LEGACY_COLOR_REPLACEMENTS[normalized] || normalized;
  }

  const cssColor = String(value ?? '').trim();
  if (/^(?:rgba?|hsla?)\([^()]+\)$/.test(cssColor)) {
    return cssColor;
  }
  return '';
}

function normalizeBrandName(value) {
  return String(value ?? '').trim();
}

function setBrandName(value) {
  activeBrandName.value = normalizeBrandName(value) || DEFAULT_BRAND_NAME;
  brandNameChangeListener?.(activeBrandName.value);
  const title = activeBrandName.value
    .replace(/-([a-z0-9])/g, (_, letter) => letter.toUpperCase())
    .replace(/^[a-z]/, m => m.toUpperCase())
  if (typeof document !== 'undefined') {
    document.title = `${title} Wallet`;
  }
}

function resolveColorKey(key) {
  const rawKey = String(key || '').trim();
  const normalizedKey = rawKey.replace(/^--wp-color-/, '');
  const kebabKey = toKebabCase(normalizedKey);
  const camelKey = kebabKey.replace(/-([a-z0-9])/g, (_, letter) => letter.toUpperCase());

  if (normalizeHex(rawKey) === '#2667FF') {
    return 'brand1';
  }
  if (COLOR_VARIABLES[camelKey]) {
    return camelKey;
  }
  if (COLOR_ALIASES[camelKey]) {
    return COLOR_ALIASES[camelKey];
  }
  return '';
}

function getFlatColorEntries(colors) {
  if (!colors || typeof colors !== 'object' || Array.isArray(colors)) {
    return [];
  }

  return Object.entries(colors).flatMap(([name, rawValue]) => {
    const keys = FLAT_CARD_TYPE_COLOR_KEYS[name];
    if (!keys) {
      return [[name, rawValue]];
    }

    const values = Array.isArray(rawValue) ? rawValue : [rawValue];
    return keys
      .filter((key, index) => values[index] !== undefined)
      .map((key, index) => [key, values[index]]);
  });
}

function readThemePayload(payload) {
  const colors = payload?.colors || {};
  const result = {};
  for (const [key, rawValue] of getFlatColorEntries(colors)) {
    const colorKey = resolveColorKey(key);
    const color = normalizeCssColor(rawValue);
    if (colorKey && color) {
      result[colorKey] = color;
    }
  }
  return result;
}

function readThemeImages(payload) {
  const images = payload?.img || {};
  if (!images || typeof images !== 'object' || Array.isArray(images)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(images).filter(([, value]) => typeof value === 'string')
  );
}

function readThemeUrls(payload) {
  const urls = payload?.url || {};
  if (!urls || typeof urls !== 'object' || Array.isArray(urls)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(urls).filter(([, value]) => typeof value === 'string')
  );
}

function readThemeEmail(payload) {
  return typeof payload?.email === 'string' ? payload.email.trim() : '';
}

function readThemeName(payload) {
  return normalizeBrandName(payload?.name);
}

function readStoredBrandName(payload) {
  const name = readThemeName(payload);
  return name && name.toLowerCase() !== LEGACY_BRAND_NAME ? name : DEFAULT_BRAND_NAME;
}

export function getBrandName() {
  return activeBrandName.value;
}

export function registerBrandNameListener(listener) {
  brandNameChangeListener = typeof listener === 'function' ? listener : null;
  brandNameChangeListener?.(activeBrandName.value);
  return () => {
    if (brandNameChangeListener === listener) {
      brandNameChangeListener = null;
    }
  };
}

export function replaceBrandName(value) {
  const brandName = activeBrandName.value;
  return String(value ?? '').replace(/(?:winpay|zorpay)/gi, (matchedName) => {
    if (matchedName === matchedName.toLowerCase()) {
      return brandName.toLowerCase();
    }
    if (matchedName === matchedName.toUpperCase()) {
      return brandName.toUpperCase();
    }
    return brandName;
  });
}

export function resolveBrandName(value, fallback = activeBrandName.value) {
  const name = normalizeBrandName(value);
  return !name || name.toLowerCase() === LEGACY_BRAND_NAME
    ? normalizeBrandName(fallback) || activeBrandName.value
    : name;
}

function setCssColors(colors) {
  if (typeof document === 'undefined') {
    return;
  }

  const root = document.documentElement;
  Object.entries(colors).forEach(([key, value]) => {
    const variable = COLOR_VARIABLES[key];
    if (variable && value) {
      root.style.setProperty(variable, value);
    }
  });
}

export const useThemeStore = defineStore('theme', {
  state: () => ({
    name: DEFAULT_BRAND_NAME,
    colors: { ...DEFAULT_THEME_COLORS },
    images: {},
    url: {},
    email: '',
    loaded: false
  }),

  getters: {
    brandName: (state) => state.name || DEFAULT_BRAND_NAME,
    color: (state) => (key, fallback = '') => state.colors[key] || fallback,
    image: (state) => (key, fallback = '') => state.images[key] || fallback,
    cssVariables: (state) => Object.fromEntries(
      Object.entries(state.colors).map(([key, value]) => [COLOR_VARIABLES[key], value])
    )
  },

  actions: {
    ensureDefaults() {
      const savedColors = readStorageJSON(THEME_STORAGE_KEY, {}, {
        storage: getStorage('local')
      }) || {};
      this.colors = {
        ...DEFAULT_THEME_COLORS,
        ...readThemePayload(savedColors)
      };
      this.images = readThemeImages(savedColors);
      this.url = readThemeUrls(savedColors);
      this.email = readThemeEmail(savedColors);
      this.name = readStoredBrandName(savedColors);
      setBrandName(this.name);
      setCssColors(this.colors);
      return this.colors;
    },

    applyFromApi(payload) {
      const nextName = readThemeName(payload);
      const nextColors = readThemePayload(payload);
      const nextImages = readThemeImages(payload);
      const nextUrls = readThemeUrls(payload);
      const nextEmail = readThemeEmail(payload);
      if (!nextName && !Object.keys(nextColors).length && !Object.keys(nextImages).length && !Object.keys(nextUrls).length && !nextEmail) {
        this.loaded = true;
        return this.colors;
      }

      if (nextName) {
        this.name = nextName;
      }
      setBrandName(this.name);
      this.colors = {
        ...this.colors,
        ...nextColors
      };
      this.images = {
        ...this.images,
        ...nextImages
      };
      this.url = {
        ...this.url,
        ...nextUrls
      };
      if (nextEmail) {
        this.email = nextEmail;
      }
      setCssColors(nextColors);
      writeStorageJSON(THEME_STORAGE_KEY, {
        name: this.name,
        colors: this.colors,
        img: this.images,
        url: this.url,
        email: this.email
      });
      this.loaded = true;
      return this.colors;
    },

    async loadFromApi({ request } = {}) {
      if (typeof request !== 'function') {
        return this.colors;
      }

      const response = await request();
      return this.applyFromApi(response);
    }
  }
});

export {
  COLOR_VARIABLES,
  LEGACY_COLOR_REPLACEMENTS,
  readThemeImages,
  readThemePayload,
  readThemeUrls,
  readThemeEmail
};
